import React, { useEffect, useState } from "react";
import { ReactComponent as SellPriceChange } from "assets/icons/sell_price_change.svg";
import WatchlistPriceChangeChart from "components/Charts/WatchlistPriceChangeChart";
import { ReactComponent as InfoIcon } from "assets/icons/info.svg";
import { Tooltip } from "react-tooltip";
import useProperties from "hooks/useProperties";
import { formatCurrency } from "utils/formatNumber";
import useBookmark from "hooks/useBookmark";
import useAuth from "hooks/useAuth";
import PropertyPopup from "components/Dialog/PropertyPopup";

const PropertyItem = ({ property }) => {
  const { setSelectedPropertyPopup } = useProperties();

  // Last sale price
  function getLastSalePrice(history) {
    if (!Array.isArray(history) || history.length === 0) return "";

    const lastEntry = history.reduce((latest, current) => {
      return new Date(current.changed_at) > new Date(latest.changed_at)
        ? current
        : latest;
    });

    return formatCurrency(lastEntry.sale_price, lastEntry.sale_currency);
  }

  return (
    <div className="h-auto lg:h-[116px] flex items-center flex-col lg:space-x-[35px] space-x-0 space-y-4 lg:space-y-0 lg:flex-row">
      <div className="flex w-full lg:w-[40%] items-center space-x-[10px]">
        <div className="w-full lg:max-w-[215px]">
          <h2 className="text-[16px] md:text-[20px] text-black font-semibold leading-[30px] line-clamp-1">
            {property?.title}
          </h2>
          <div className="flex mt-1 items-center capitalize space-x-[6px] text-[12px] md:text-[14px] font-normal text-[#9B9B9B] leading-[12px]">
            {property?.location?.name}
          </div>
          <h1 className="text-black text-[16px] md:text-[22px] font-bold leading-[22px] mt-[10px]">
            {formatCurrency(property?.sale_price, property?.sale_currency)}
          </h1>
          <h3 className="text-black leading-[22px] text-[14px] md:text-[16px] line-through">
            {getLastSalePrice(property?.history)}
          </h3>
        </div>
        <div className="w-full lg:w-[182px] h-[108px] rounded-[5px]">
          <img
            src={property?.pictures[0]}
            alt="property"
            className="w-full h-full rounded-[5px] object-cover"
          />
        </div>
      </div>
      <div className="flex items-center flex-col sm:flex-row w-full lg:w-[60%] h-full sm:space-x-[25px] space-y-[10px] sm:space-y-0">
        <WatchlistPriceChangeChart property={property} />
        <button
          onClick={() =>
            setSelectedPropertyPopup({ property, id: "watchListProperty" })
          }
          className="bg-primary w-full sm:w-[93px] rounded-[7px] h-[36px] sm:h-[116px] text-[15px] text-white font-medium text-center leading-[24px] -tracking-[0.165px]"
        >
          Vedi Casa
        </button>
      </div>
    </div>
  );
};

const WatchlistProperties = () => {
  const [showAllProperty, setShowAllProperty] = useState(false);
  const initialDisplayCount = 2; // Initally 2 property will display
  const {
    bookmarkedPropertyIds,
    loadBookmarks,
    getRecentPriceChangedProperty,
    isFetchingRecentPriceChangedProperty,
    recentPriceChangedProperties,
  } = useBookmark();
  const { user } = useAuth();

  const toggleShowAllProperty = () => {
    setShowAllProperty(!showAllProperty);
  };

  useEffect(() => {
    loadBookmarks();
    getRecentPriceChangedProperty();
  }, [user]);

  const displayedProperties = showAllProperty
    ? recentPriceChangedProperties?.results || []
    : recentPriceChangedProperties?.results?.slice(0, initialDisplayCount) ||
      [];

  return (
    <div className="bg-white rounded-[15px] h-auto pb-5 pl-5 pt-[21px] pr-[23px]">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-[10px]">
          <div className="min-w-[52px] h-[52px] rounded-full bg-[#E8F6FF] flex items-center justify-center">
            <SellPriceChange />
          </div>

          <h1 className="text-[16px] md:text-[22px] font-semibold leading-[19.36px] md:leading-[30px] text-black max-w-[437px]">
            {bookmarkedPropertyIds?.length < 1 ? (
              "Nessuna proprietà salvata nell’organizer"
            ) : displayedProperties < 1 ? (
              <>
                Nessuna proprietà nella tua watchlist ha subito una variazione
                di <span className="text-primary">prezzo</span> recentemente
              </>
            ) : (
              <>
                Alcune proprietà nella tua watchlist hanno subito una variazione
                di <span className="text-primary">prezzo</span>
              </>
            )}
          </h1>
        </div>

        {/* If no property then only display this info icon  */}
        {displayedProperties.length < 1 && (
          <div
            data-tooltip-id="watchlist_tooltip"
            data-tooltip-content="Qui verranno mostrate tutte le proprietà salvate nell’organizer che nelle ultime 2 settimane hanno subito una variazione di prezzo"
            className="mr-[6px] cursor-pointer hidden sm:block "
          >
            <InfoIcon />
          </div>
        )}
        {/* Info Tooltip */}
        <Tooltip
          id="watchlist_tooltip"
          place="top"
          className="!max-w-64 !z-10 !text-sm !text-gray-500 !bg-white border !border-gray-200 !rounded-lg !shadow-sm"
        />
      </div>

      {displayedProperties?.length > 0 && (
        <hr className="mt-[18px] bg-[#D7D7D7] w-full h-[1px] mb-[16px]" />
      )}
      <div className="space-y-[44px]">
        {displayedProperties.length > 0 &&
          displayedProperties?.map((property, index) => (
            <PropertyItem key={property?._id} property={property} />
          ))}
      </div>
      {recentPriceChangedProperties?.results?.length > initialDisplayCount && (
        <>
          <hr className="mt-[27px] bg-[#D7D7D7] w-full h-[1px] mb-[15px]" />
          <button
            onClick={toggleShowAllProperty}
            className="h-[60px] rounded-[10px] bg-primary text-center text-[15px] underline font-medium text-white w-full"
          >
            {showAllProperty ? "Mostra Meno" : "Mostra Lista Completa"}
          </button>
        </>
      )}

      {/* Property card on click mini cell*/}

      <PropertyPopup popupType="watchListProperty" />
    </div>
  );
};

export default WatchlistProperties;
